import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { enumIsfShipmentCode } from '@app/data-layer/enums/isf';
import { ShipmentModel } from '@data-layer/models/common';
import { debounceTime, switchMap, take, takeUntil } from 'rxjs/operators';
import { IsfStateService } from '../../../services';
import { IsfModel } from '@data-layer/models/isf';

@Component({
  selector: 'desktop-shipment-details-bl',
  template: `
    <p>{{ 'LABELS.CHOOSE_TYPE_OF_BL' | translate }}</p>

    <form [formGroup]="formGroup" autocomplete="off">
      <div class="d-flex justify-content-between">
        <div class="card card-step bl-type" [ngClass]="{ 'is-selected': blControl.value === enumIsfShipmentCode.OCEAN }">
          <div class="card-header">
            <app-radio-button
              [control]="blControl"
              [id]="'bl-type-master'"
              [label]="'FORM_LABEL.MASTER'"
              [value]="enumIsfShipmentCode.OCEAN"
              [name]="'bl-type'"
            ></app-radio-button>
          </div>
          <div class="card-body px-3">
            <span>{{ 'FORM_HELPER.FULL_CONTAINER' | translate }}</span>
          </div>
        </div>
        <div class="card card-step bl-type" [ngClass]="{ 'is-selected': blControl.value === enumIsfShipmentCode.HOUSE }">
          <div class="card-header">
            <app-radio-button
              [control]="blControl"
              [id]="'bl-type-house'"
              [label]="'FORM_LABEL.HOUSE'"
              [value]="enumIsfShipmentCode.HOUSE"
              [name]="'bl-type'"
            ></app-radio-button>
          </div>
          <div class="card-body px-3">
            <span>{{ 'FORM_HELPER.LESS_THAN_CONTAINER_LOAD' | translate }}</span>
          </div>
        </div>
      </div>

      <div class="mt-3">
        <div class="bl-type-info">
          <p>
            {{
              (blControl.value === enumIsfShipmentCode.HOUSE
                ? 'LABELS.FILL_IN_YOUR_AMS_HOUSE_INFORMATION'
                : 'LABELS.FILL_IN_YOUR_AMS_MASTER_INFORMATION'
              ) | translate
            }}
          </p>
        </div>

        <div class="form-row">
          <div class="col-3">
            <app-control-wrapper [control]="billFormGroup.get('carrier')" [errors]="errors.billCarrier">
              <label for="billCarrier" control-label>{{ 'LABELS.CARRIER' | translate }}</label>
              <input
                type="text"
                [formControl]="billFormGroup.get('carrier')"
                class="form-control"
                id="billCarrier"
                name="billCarrier"
                [placeholder]="'FORM_PLACEHOLDER.CARRIER_CODE' | translate"
                [mask]="'S{4}'"
                [appMaskErrorTooltip]="'MASK_ERROR.ONLY_ENGLISH'"
                [maxMaskLength]="4"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
          <div class="col-9">
            <app-control-wrapper [control]="billFormGroup.get('number')" [errors]="errors.billNumber">
              <label for="billCarrier" control-label>{{ 'LABELS.BL' | translate }}</label>
              <input
                type="text"
                [formControl]="billFormGroup.get('number')"
                class="form-control"
                id="billNumber"
                name="billNumber"
                [placeholder]="'FORM_PLACEHOLDER.BILL_NUMBER' | translate"
                [mask]="'A{12}'"
                [appMaskErrorTooltip]="'MASK_ERROR.ONLY_ENGLISH_DIGITS'"
                [maxMaskLength]="12"
                control-wrapper
              />
            </app-control-wrapper>
          </div>
        </div>
      </div>

      <div class="bl-form-buttons" *ngIf="isSideBar">
        <button type="submit" class="btn btn-primary m-auto" [disabled]="formGroup.invalid" (click)="saveOnButtonClick()">
          {{ 'BUTTON.SAVE' | translate }}
        </button>
      </div>
    </form>
  `,
  styleUrls: ['./desktop-shipment-details-bl.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopShipmentDetailsBlComponent implements OnInit, OnDestroy {
  @Input() public isSideBar: boolean;
  @Output() public dataSaved: EventEmitter<void> = new EventEmitter<void>();
  @Output() public saveBLData: EventEmitter<ShipmentModel> = new EventEmitter<ShipmentModel>();
  @Output() public formValidityUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  public formGroup: UntypedFormGroup;
  public blControl: UntypedFormControl;
  public billFormGroup: UntypedFormGroup;
  public errors: { [name: string]: IErrorForm } = {
    billCarrier: {
      required: 'FORM_ERROR.REQUIRED',
      wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
      minlength: 'FORM_ERROR.MIN_LENGTH',
      maxlength: 'FORM_ERROR.MAX_LENGTH',
    },
    billNumber: {
      required: 'FORM_ERROR.REQUIRED',
      wrongReferenceNumberShip: 'FORM_ERROR.WRONG_REFERENCE_NUMBER_SHIP',
      minlength: 'FORM_ERROR.MIN_LENGTH',
      maxlength: 'FORM_ERROR.MAX_LENGTH',
    },
  };
  public enumIsfShipmentCode: typeof enumIsfShipmentCode = enumIsfShipmentCode;
  public shipment: ShipmentModel;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private fh: FormHelper, private isfStateService: IsfStateService) {}

  public ngOnInit(): void {
    this.formGroup = this.fh.createIsfShipmentDetailsFormGroup();
    this.blControl = this.formGroup.get('code') as UntypedFormControl;
    this.billFormGroup = this.formGroup.get('bill') as UntypedFormGroup;

    this.billFormGroup.valueChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.formValidityUpdated.emit(this.billFormGroup.valid));

    this.patchData();

    if (!this.isSideBar) {
      this.saveOnFormUpdate();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public saveOnButtonClick(): void {
    this.updateData()
      .pipe(
        switchMap(() => this.isfStateService.getSummaryById(this.isfStateService?.currentIsf?.referenceNumber)),
        take(1),
      )
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;
        this.dataSaved.emit();
      });
  }

  private updateData() {
    return this.isfStateService.updateShipment(this.prepareData(), !!this.isSideBar);
  }

  private prepareData(): ShipmentModel {
    return {
      ...this.isfStateService.currentIsf.shipment,
      code: this.blControl.value,
      masterBill: this.blControl.value !== enumIsfShipmentCode.HOUSE ? this.formGroup.get('bill').value : { carrier: null, number: null },
      houseBill: this.blControl.value === enumIsfShipmentCode.HOUSE ? this.formGroup.get('bill').value : { carrier: null, number: null },
    };
  }

  private patchData() {
    const shipment: ShipmentModel = this.isfStateService.getShipment();
    this.shipment = shipment;

    this.fh.patchIsfShipmentDetailsFormGroup(this.formGroup, this.isfStateService.currentIsf?.type, this.shipment);
  }

  private saveOnFormUpdate(): void {
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((shipment) => this.saveBLData.emit(this.prepareData()));
  }
}
